import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardList,
  EmptyState,
  ErrorState,
  Modal,
  Page,
  PageHeader,
  Skeleton,
} from '@indipia/ui-core';
import { billingApi } from '../api/billingApi';
import { generateCustomerKey, requestBillingAuth } from '../lib/toss';
import { PaymentMethodTile } from '../components/PaymentMethodTile';
import { buildDefaultCardAlias } from '../components/PaymentMethodCardBody';

/** BE 에러 응답의 실제 사유(BusinessException의 data 필드, C-03 §4.3 관례)를 뽑아낸다.
 *  없으면 message, 그마저 없으면 fallback. */
function extractErrorReason(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { data?: string; message?: string } } })?.response?.data;
  return data?.data ?? data?.message ?? fallback;
}

export function PaymentMethodPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  // 등록 실패는 토스트만으로는 놓치기 쉽다(닫히는 위젯 자체가 "성공한 느낌"을 줌) — 배너로 화면에
  // 남겨서 사용자가 다시 시도하거나 직접 닫기 전까지 계속 보이게 한다.
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const processedAuthKeyRef = useRef<string | null>(null);

  const paymentMethodsQuery = useQuery({
    queryKey: ['billing', 'paymentMethods'],
    queryFn: async ({ signal }) => (await billingApi.getPaymentMethods(signal)).data.data,
  });

  const companyInfoQuery = useQuery({
    queryKey: ['billing', 'companyInfo'],
    queryFn: async ({ signal }) => (await billingApi.getCompanyInfo(signal)).data.data,
  });

  const deleteMutation = useMutation({
    mutationFn: (paymentMethodId: number) => billingApi.deletePaymentMethod(paymentMethodId),
    onSuccess: () => {
      toast.success('결제수단을 삭제했어요');
      setPendingDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    },
    onError: (error: unknown) => {
      toast.error(extractErrorReason(error, '삭제에 실패했습니다'));
      setPendingDeleteId(null);
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (paymentMethodId: number) => billingApi.setDefaultPaymentMethod(paymentMethodId),
    onSuccess: () => {
      toast.success('기본 결제수단으로 설정했어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    },
    onError: () => toast.error('설정에 실패했습니다. 잠시 후 다시 시도해주세요.'),
  });

  const updateAliasMutation = useMutation({
    mutationFn: (payload: { paymentMethodId: number; cardAlias: string }) =>
      billingApi.updateCardAlias(payload.paymentMethodId, payload.cardAlias),
    onSuccess: () => {
      toast.success('별칭을 저장했어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    },
    onError: (error: unknown) => toast.error(extractErrorReason(error, '별칭 저장에 실패했습니다')),
  });

  // 토스 결제창이 등록 성공/실패 후 이 페이지로 돌아오며 쿼리스트링에 결과를 실어보낸다.
  // authKey는 여기서 소비 즉시 무의미해지는 1회성 값이라 처리 후 바로 URL에서 지운다(새로고침 시 재요청 방지).
  const registerCardMutation = useMutation({
    mutationFn: (payload: { authKey: string; customerKey: string }) => billingApi.registerCard(payload),
    onSuccess: async (response) => {
      setRegistrationError(null);
      toast.success('카드가 등록되었어요');
      // 등록 직후엔 별칭이 비어 있어(BE cardAlias=null) "카드사명 + 뒷자리 4자리"를 기본값으로
      // 바로 저장한다(2026-09-21, 사용자 요청) — 실패해도 등록 자체는 이미 끝났으니 조용히 무시.
      const registered = response.data.data;
      if (registered) {
        try {
          await billingApi.updateCardAlias(registered.paymentMethodId, buildDefaultCardAlias(registered));
        } catch {
          // no-op
        }
      }
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    },
    onError: (error: unknown) => {
      const reason = extractErrorReason(error, '카드 등록에 실패했습니다');
      toast.error(reason);
      setRegistrationError(reason);
    },
  });

  useEffect(() => {
    const authKey = searchParams.get('authKey');
    const customerKey = searchParams.get('customerKey');
    if (authKey && customerKey) {
      // React StrictMode 이중 실행 방지(AuthCallbackPage.tsx와 동일 패턴) — authKey는 1회성 토큰이라
      // 같은 값으로 두 번 호출하면 두 번째는 토스에서 "존재하지 않는 정보"로 실패한다.
      if (processedAuthKeyRef.current === authKey) return;
      processedAuthKeyRef.current = authKey;
      registerCardMutation.mutate({ authKey, customerKey });
      setSearchParams({}, { replace: true });
      return;
    }
    const failMessage = searchParams.get('message');
    if (failMessage) {
      toast.error(failMessage);
      setRegistrationError(failMessage);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const methods = paymentMethodsQuery.data;

  return (
    <Page width="lg">
      <PageHeader
        title="결제수단 등록·변경"
        subtitle="기본 결제수단으로 매월 자동결제됩니다"
        onBack={() => navigate('/agents/billing/manage')}
      />

      {/* 등록된 결제수단 목록 */}
      <Card className="mt-06">
        <h3 className="text-title-sm text-text">등록된 결제수단</h3>

        {paymentMethodsQuery.isLoading ? (
          <div className="mt-04 flex gap-md overflow-x-auto">
            <Skeleton className="w-64 shrink-0" height={140} />
            <Skeleton className="w-64 shrink-0" height={140} />
          </div>
        ) : paymentMethodsQuery.isError ? (
          <ErrorState className="mt-04" title="결제수단 정보를 불러오지 못했습니다" />
        ) : methods && methods.length > 0 ? (
          <CardList layout="row" className="mt-04">
            {methods.map((method) => (
              <PaymentMethodTile
                key={method.paymentMethodId}
                method={method}
                onSetDefault={() => setDefaultMutation.mutate(method.paymentMethodId)}
                onDelete={() => setPendingDeleteId(method.paymentMethodId)}
                onSaveAlias={(cardAlias) => updateAliasMutation.mutate({ paymentMethodId: method.paymentMethodId, cardAlias })}
                settingDefault={setDefaultMutation.isPending}
                savingAlias={
                  updateAliasMutation.isPending &&
                  updateAliasMutation.variables?.paymentMethodId === method.paymentMethodId
                }
              />
            ))}
          </CardList>
        ) : (
          <EmptyState
            className="mt-04"
            title="등록된 결제수단이 없어요"
            description="아래에서 카드를 등록해주세요."
          />
        )}
      </Card>

      {/* 새 결제수단 추가 — 카드(토스페이먼츠 연동)만 지원한다. 계좌이체·가상계좌 탭은 제거함
          (사용자 요청, 2026-09-16). */}
      <Card className="mt-06">
        <h3 className="text-title-sm text-text">새 결제수단 추가</h3>
        <p className="mt-01 text-body-sm text-text-muted">카드를 등록할 수 있어요</p>

        {registrationError && (
          <Alert
            kind="error"
            className="mt-04"
            title="카드 등록에 실패했어요"
            onClose={() => setRegistrationError(null)}
          >
            {registrationError}
          </Alert>
        )}

        <CardRegistrationForm
          customerName={companyInfoQuery.data?.compRepName ?? undefined}
          customerEmail={companyInfoQuery.data?.compEmail ?? undefined}
          onStart={() => setRegistrationError(null)}
        />

        <div className="mt-06 flex flex-wrap items-center gap-sm">
          <Badge status="mute">준비중</Badge>
          <span className="text-body-sm text-text-muted">카카오페이</span>
          <Badge status="mute" className="ml-04">준비중</Badge>
          <span className="text-body-sm text-text-muted">네이버페이</span>
        </div>
        <p className="mt-02 text-caption text-text-muted">
          카카오페이·네이버페이는 매달 자동으로 빠져나가는 정기결제 지원 여부를 확인한 뒤 순서대로 열 예정입니다.
          카드 정보는 결제대행사(PG)를 통해 암호화되어 처리되며, 인디피아는 카드번호를 직접 저장하지 않습니다.
        </p>
      </Card>

      <Modal open={pendingDeleteId != null} onClose={() => setPendingDeleteId(null)} title="결제수단을 삭제할까요?">
        <div className="flex flex-col gap-sm">
          <p className="text-body-sm text-text-muted">삭제한 결제수단은 되돌릴 수 없어요.</p>
          <div className="mt-03 flex w-full gap-sm">
            <Button variant="secondary" className="flex-1" onClick={() => setPendingDeleteId(null)}>
              취소
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              loading={deleteMutation.isPending}
              disabled={deleteMutation.isPending}
              onClick={() => pendingDeleteId != null && deleteMutation.mutate(pendingDeleteId)}
            >
              삭제
            </Button>
          </div>
        </div>
      </Modal>
    </Page>
  );
}

interface CardRegistrationFormProps {
  customerName?: string;
  customerEmail?: string;
  /** 새 시도 시작 시 호출 — 이전 실패 배너가 새 시도 결과와 섞여 보이지 않도록 미리 지운다 */
  onStart: () => void;
}

/** 카드번호 원본은 인디피아 서버/DOM을 거치지 않는다 — 토스페이먼츠가 여는 결제창에서 직접
 *  입력받고, 유효성(카드 정지·한도초과 등)도 그 결과로 서버가 빌링키 발급을 요청할 때 PG가
 *  판정한다. 계좌이체(퀵계좌이체) 등록 지원은 2026-09-16 제거(사용자 요청) — 카드 전용으로
 *  되돌아왔다. */
function CardRegistrationForm({ customerName, customerEmail, onStart }: CardRegistrationFormProps) {
  const [starting, setStarting] = useState(false);

  const handleStart = async () => {
    onStart();
    setStarting(true);
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      await requestBillingAuth({
        method: 'CARD',
        customerKey: generateCustomerKey(),
        successUrl: returnUrl,
        failUrl: returnUrl,
        customerName,
        customerEmail,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '등록을 시작하지 못했어요');
      setStarting(false);
    }
  };

  return (
    <div className="mt-05 flex flex-col gap-md">
      <p className="text-body-sm text-text-muted">
        카드 등록하기를 누르면 토스페이먼츠의 안전한 결제창으로 이동해요. 카드번호는 인디피아 서버에
        저장되지 않고, 정상 카드인지도 그 자리에서 바로 확인돼요.
      </p>
      <Button
        variant="primary"
        className="w-full sm:w-auto"
        onClick={handleStart}
        loading={starting}
        disabled={starting}
      >
        카드 등록하기
      </Button>
    </div>
  );
}
